import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pause, Play, AlertCircle } from 'lucide-react';
import { Button, Dialog, Input, Textarea } from '@/design-system';
import { managerConnectorService } from './api/connector-gateways.service';

interface Props {
  gatewayId: string;
  /** ISO 8601 quando pausado; null se ativo. */
  pausedUntil: string | null;
  pausedReason: string | null;
  /** Só renderiza botões se true. */
  canManage: boolean;
}

/**
 * P3.3 (issue #62): controle pra pausar/retomar auto-update num gateway.
 * Estado pausado é exibido como badge + botão "Retomar"; estado ativo
 * mostra botão "Pausar atualizações…" que abre dialog.
 */
export function PauseUpdatesControl({
  gatewayId,
  pausedUntil,
  pausedReason,
  canManage,
}: Props) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [untilLocal, setUntilLocal] = useState('');
  const [reason, setReason] = useState('');

  const isPaused = !!pausedUntil && new Date(pausedUntil) > new Date();

  const pauseMutation = useMutation({
    mutationFn: (payload: { until: string; reason?: string }) =>
      managerConnectorService.pauseUpdates(gatewayId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['manager-connector-gateways'] });
      setDialogOpen(false);
      setUntilLocal('');
      setReason('');
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () => managerConnectorService.resumeUpdates(gatewayId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['manager-connector-gateways'] });
    },
  });

  if (!canManage && !isPaused) {
    return null; // staff sem permissão e sem pausa ativa: nada a mostrar
  }

  return (
    <>
      {isPaused ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertCircle className="h-4 w-4 text-amber-600" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-xs">
            <p className="font-medium text-amber-900">
              Updates pausados até {new Date(pausedUntil!).toLocaleString('pt-BR')}
            </p>
            {pausedReason && (
              <p className="text-amber-700">{pausedReason}</p>
            )}
          </div>
          {canManage && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => resumeMutation.mutate()}
              disabled={resumeMutation.isPending}
              className="border-amber-300 text-amber-800 hover:bg-amber-100"
            >
              <Play size={14} className="mr-1.5" aria-hidden="true" />
              Retomar
            </Button>
          )}
        </div>
      ) : canManage ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setDialogOpen(true)}
          className="border-gray-300 text-gray-700 hover:bg-gray-100"
        >
          <Pause size={14} className="mr-1.5" aria-hidden="true" />
          Pausar atualizações…
        </Button>
      ) : null}

      <Dialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Pausar atualizações"
        footer={(
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setDialogOpen(false)} disabled={pauseMutation.isPending}>Cancelar</Button>
            <Button size="sm" variant="primary" disabled={!untilLocal || pauseMutation.isPending} onClick={() => {
              const untilIso = new Date(untilLocal).toISOString();
              pauseMutation.mutate({ until: untilIso, reason: reason.trim() || undefined });
            }}>
              {pauseMutation.isPending ? 'Pausando…' : 'Pausar'}
            </Button>
          </div>
        )}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Auto-update fica pausado até a data informada. Force update
            individual ainda funciona.
          </p>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Pausar até (data e hora)
            </label>
            <Input
              type="datetime-local"
              value={untilLocal}
              onChange={(e) => setUntilLocal(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Motivo (opcional)
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: Black Friday — adiar até 26/12"
              rows={2}
              maxLength={280}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
}
